import React, { useEffect, useContext, useRef, useState } from "react";
import {
  MantineProvider,
  Box,
  Container,
  Paper,
  Image,
  Center,
  Stack,
  Text,
  Group,
  Divider,
  Anchor,
  ActionIcon,
  LoadingOverlay,
  Global,
  useMantineTheme,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";

import { Notifications } from "@mantine/notifications";
import { useMediaQuery } from "@mantine/hooks";
import VXLogo from "./assets/vx-logo.svg";
import { DataContext, routeSequenceNeedsFaceModels } from "./context/DataContext";
import { ensureFaceDetectionWarmUp } from "./hooks/useFaceDetection";
import useLocale from "./hooks/useLocale";
import Navigation from "./components/navigation";
import NoticeConsentPrompt from "./components/notice-consent-prompt";
import { resolveConsentScreen } from "./utils/consentScreen";
import PasskeyPrompt from "./components/passkey-prompt";
import OrientationWarning from "./components/orientation-warning";
import PasskeyFinalizePrompt from "./components/passkey-finalize-prompt";
import FontLoaderSimple from "./components/FontLoaderSimple";
import { ctaButtonRootStyles, secondaryButtonRootStyles, normalizeColor } from "./helpers/component-styles";
import {
  passkeysSupported,
  platformAuthenticatorAvailable,
  isSameOriginWithAncestors,
  shouldPrompt as fetchShouldPrompt,
  tryPasskeyBypass,
  registerPasskey,
  recordPasskeyEvent,
} from "./helpers/passkey";
import { isMobile } from "react-device-detect";
import { postToPublisher } from "./utils/publisherMessage";
import { isSelfieSupplied } from "./utils/suppliedImages";
import { applyDocumentLanguage, getTextDirection } from "./utils/textDirection";
const isIframe = window !== window.top;
import "./App.css";
import { watchSdkMedia } from "./utils/sdkMediaAccessibility";

const FALLBACK_STACK =
  'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

function familyStack(family) {
  if (!family) return FALLBACK_STACK;
  const quoted = family.includes(" ") ? `"${family}"` : family;
  return `${quoted}, ${FALLBACK_STACK}`;
}

// Read backgroundColor from URL params for first-paint application before data loads.
// Matches the convention in root.js (hex with or without leading #).
function urlBackgroundColor() {
  const raw = new URLSearchParams(window.location.search).get('backgroundColor');
  if (!raw) return null;
  return raw.startsWith('#') ? raw : `#${raw}`;
}

const IS_PASSKEY_SETUP_POPUP =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("passkey_setup") === "1";

// Window the iframe opens to run WebAuthn create() at top level. Same URL,
// same get_session (so settings.passkey.session_token + binding fields all
// arrive normally), but skips the journey entirely and posts the result
// back to the opener iframe before closing itself.
function PasskeySetupPopupContent({ passkeyArgs, data }) {
  const [state, setState] = useState("prompt");
  const ready = !!passkeyArgs.sessionToken;

  const postResult = (success, errorMessage) => {
    try {
      postToPublisher(
        {
          type: "passkey_setup_result",
          success,
          error: errorMessage,
        },
        { target: "opener", trigger: "passkey_popup" },
      );
    } catch {
      // opener may be gone or cross-origin restricted — best-effort
    }
  };

  const handleContinue = async () => {
    // The popup never shows its own success or error screen — the moment
    // the ceremony resolves, it posts the result back and closes. The
    // iframe (opener) is in the loading state waiting; it'll flip to
    // success or error based on the message we send. This avoids the
    // jarring two-window "Passkey saved! ... Passkey saved!" sequence.
    setState("loading");
    const fullName =
      data?.fn || data?.ln
        ? [data?.fn, data?.ln].filter(Boolean).join(" ")
        : undefined;
    const hasAddressParts =
      data?.addr || data?.city || data?.state || data?.zip;
    const address = hasAddressParts
      ? {
          line1: data?.addr || undefined,
          city: data?.city || undefined,
          region: data?.state || undefined,
          postal: data?.zip || undefined,
        }
      : undefined;
    const result = await registerPasskey({
      ...passkeyArgs,
      binding: {
        full_name: fullName,
        address,
        phone: data?.phone || undefined,
        dob: data?.dob || undefined,
      },
    });
    if (result.registered) {
      postResult(true);
    } else {
      postResult(false, "registration_failed");
    }
    window.close();
  };

  const handleSkip = () => {
    postResult(false, "user_skipped");
    window.close();
  };

  if (!ready) {
    return <LoadingOverlay visible />;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 380,
          height: "100%",
          maxHeight: 620,
          display: "flex",
          flexDirection: "column",
          padding: "20px 25px 0",
        }}
      >
        <PasskeyFinalizePrompt
          state={state}
          onContinue={handleContinue}
          onSkip={handleSkip}
        />
      </div>
    </div>
  );
}

// Inner component that can use the theme
function AppContent({ routes }) {
  const { text, data, settings, status, dispatch, postMessage, routeSequence } =
    useContext(DataContext);
  // Footer strings via the local `footer` locale namespace so
  // sr-Latn, sr-Cyrl, plain sr etc. resolve to the right script
  // file. Backend `text.general.*` came back in the wrong script
  // for sr-Latn sessions — see PR #349.
  const footerLocale = useLocale("footer");
  const theme = useMantineTheme();
  // The Mitek SDK appends its hidden <video> container to <body> when a
  // capture starts and re-creates it on retries; keep it out of the
  // accessibility tree for the life of the app (Zendesk #1797865 #14/#15).
  useEffect(() => watchSdkMedia(), []);
  // When the iframe opened us as a top-level popup specifically to run the
  // WebAuthn create() ceremony (which Safari + Chrome 130+ won't run inside
  // cross-origin iframes), skip the entire journey UI and render the
  // dedicated passkey setup screen instead.
  const popupPasskeyArgs = {
    sessionToken: settings?.passkey?.session_token,
    request_id: settings?.request_id,
    workspace: settings?.passkey?.workspace,
    email: data?.email,
  };
  if (IS_PASSKEY_SETUP_POPUP) {
    return <PasskeySetupPopupContent passkeyArgs={popupPasskeyArgs} data={data} />;
  }
  const customerBg = data?.styles?.global?.backgroundColor ?? urlBackgroundColor();
  const customerDesktopBg = data?.styles?.global?.desktopBackgroundColor;
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const isWideViewport = useMediaQuery("(min-width: 600px)");

  // Top-level verification notice gate — when the publisher's settings include
  // `verification_notice_enabled: true`, this screen renders as the user's
  // first experience, before any journey routes. The Inside US vs Outside US
  // variant is picked from `data.ip_country` ("US" → Inside US, anything
  // else → Outside US). Local state flips once the user clicks Continue so
  // the routes can render for the rest of the session.
  // Per-session overrides (k-ID SOW VT-KID-2026-002): `data.notice_and_consent.screen_customization`
  // customizes the screen and turns it on; `data.notice_and_consent.privacy_attestation`
  // (Change Order 1) skips it. Rules live in utils/consentScreen.js.
  const consentScreen = resolveConsentScreen({ settings, data });
  const verificationNoticeEnabled = consentScreen.enabled;
  // `?force_international=true` URL param overrides ip_country so the Outside US
  // variant can be previewed/tested from a US IP.
  const forceInternational =
    new URLSearchParams(window.location.search).get("force_international") === "true";
  const isOutsideUS = forceInternational || data?.ip_country !== "US";
  // `?notice_shown=true` — handoff URLs (compact, full, and browser)
  // carry this flag so the mobile receiver skips the notice since the
  // user already saw and consented on the desktop side.
  const noticeAlreadyShown =
    new URLSearchParams(window.location.search).get("notice_shown") === "true";
  const [consentDismissed, setConsentDismissed] = useState(noticeAlreadyShown);
  const showVerificationNotice = verificationNoticeEnabled && !consentDismissed;

  // Face-model shader warm-up, gated on the consent screen being gone.
  // The weights download starts earlier (DataContext, network-only), but
  // warm-up compiles WebGL shaders synchronously on the main thread —
  // 30s+ on first-visit iOS — and froze the consent button when it ran
  // during that screen. Idle-scheduled so the post-consent render paints
  // before the compile starts.
  useEffect(() => {
    if (showVerificationNotice) return;
    if (!routeSequenceNeedsFaceModels(routeSequence)) return;
    // Journey API prefill with the selfie supplied: no face capture runs,
    // and the compile would stall the processing screen.
    if (isSelfieSupplied(settings)) return;
    const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 300));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = schedule(() => ensureFaceDetectionWarmUp().catch(() => {}));
    return () => cancel(id);
  }, [showVerificationNotice, routeSequence]);

  // Passkey integration. The backend ships a `settings.passkey` block with
  // both the profile config (binding_extras, scopes, lifetime, etc.) and the
  // per-session runtime fields (workspace, short-lived session_token), plus
  // `data.email`. We hit /should-prompt on mount to decide whether to offer
  // the use/bypass flow, then offer the create/registration flow at the end
  // of a successful verification. The whole feature is a no-op when
  // settings.passkey.enabled is not set.
  const passkeyConfig = settings?.passkey;
  const passkeyEnabled =
    passkeyConfig?.enabled === true && !!passkeyConfig?.session_token;
  const passkeyArgs = {
    sessionToken: passkeyConfig?.session_token,
    request_id: settings?.request_id,
    workspace: passkeyConfig?.workspace,
    email: data?.email,
  };

  const [shouldPromptResult, setShouldPromptResult] = useState(null);
  const [showUsePrompt, setShowUsePrompt] = useState(false);
  const [showCreatePrompt, setShowCreatePrompt] = useState(false);
  const [passkeyState, setPasskeyState] = useState("prompt");
  const [useDismissed, setUseDismissed] = useState(false);
  const [createDismissed, setCreateDismissed] = useState(false);
  const [bypassedViaUse, setBypassedViaUse] = useState(false);
  const [bypassDebugPayload, setBypassDebugPayload] = useState(null);

  // One-shot guards so we don't double-fire OFFERED across re-renders.
  const useOfferedFiredRef = useRef(false);
  const createOfferedFiredRef = useRef(false);

  // Hold the route sequence back while /should-prompt is in flight so the
  // user doesn't see the verification UI flash before we swap it for the
  // use-passkey prompt. We're "checking" when passkey is enabled, we have
  // the inputs to make the call, the browser supports WebAuthn, and we
  // don't yet have a result.
  const willCallShouldPrompt =
    passkeyEnabled &&
    !!passkeyArgs.request_id &&
    !!passkeyArgs.workspace &&
    passkeysSupported();
  const passkeyChecking = willCallShouldPrompt && !shouldPromptResult;

  // Journey start: decide whether to offer the bypass flow.
  useEffect(() => {
    if (!passkeyEnabled || shouldPromptResult || !passkeysSupported()) return;
    if (!passkeyArgs.request_id || !passkeyArgs.workspace) return;
    let cancelled = false;
    fetchShouldPrompt(passkeyArgs).then((result) => {
      if (cancelled) return;
      setShouldPromptResult(result);
      const hasHints =
        Array.isArray(result?.credential_hints) &&
        result.credential_hints.length > 0;
      if (result?.prompt && hasHints && !useDismissed) {
        setShowUsePrompt(true);
        setPasskeyState("prompt");
      }
    });
    return () => {
      cancelled = true;
    };
    // Re-run only when the underlying enablement flips; arg fields are
    // derived from the same data block, so a single guard is enough.
  }, [passkeyEnabled, passkeyArgs.request_id, passkeyArgs.workspace]);

  // Eagerly gate DataContext's terminal-status redirect the moment we know
  // passkey is enabled. Without this, when status flips to "PASS" the
  // redirect fires in the same render cycle as our create-prompt decision,
  // and the publisher closes the iframe before the create UI mounts. The
  // status === "PASS" effect below releases the gate when we decide not to
  // show create (FAIL, already bypassed, no platform authenticator, etc.).
  useEffect(() => {
    console.log("[passkey] enablement check:", {
      passkeyEnabled,
      hasConfig: !!passkeyConfig,
      enabledFlag: passkeyConfig?.enabled,
      hasSessionToken: !!passkeyConfig?.session_token,
      workspace: passkeyConfig?.workspace,
      requestId: settings?.request_id,
      email: data?.email,
    });
    if (passkeyEnabled) {
      dispatch({ type: "SET_PASSKEY_CREATE_PENDING", payload: true });
    }
  }, [passkeyEnabled, dispatch, passkeyConfig, settings?.request_id, data?.email]);

  // Journey terminus: either show the create prompt or release the gate so
  // the redirect/postMessage can fire.
  useEffect(() => {
    if (status !== "PASS" && status !== "FAIL") {
      console.log("[passkey] terminus effect: status not terminal yet", { status });
      return;
    }
    console.log("[passkey] terminus effect: status =", status, {
      passkeyEnabled,
      createDismissed,
      bypassedViaUse,
      showCreatePrompt,
    });
    if (!passkeyEnabled) {
      console.log("[passkey] terminus effect: passkey not enabled, no create offer");
      return;
    }

    if (status === "FAIL") {
      console.log("[passkey] terminus effect: status FAIL, releasing gate");
      dispatch({ type: "SET_PASSKEY_CREATE_PENDING", payload: false });
      return;
    }

    if (createDismissed || bypassedViaUse) {
      console.log("[passkey] terminus effect: dismissed or bypassed, releasing gate");
      dispatch({ type: "SET_PASSKEY_CREATE_PENDING", payload: false });
      return;
    }

    if (showCreatePrompt) {
      console.log("[passkey] terminus effect: create prompt already showing");
      return;
    }

    let cancelled = false;
    platformAuthenticatorAvailable().then((available) => {
      if (cancelled) return;
      console.log("[passkey] platformAuthenticatorAvailable ->", available);
      if (!available) {
        console.log("[passkey] no platform authenticator, releasing gate");
        dispatch({ type: "SET_PASSKEY_CREATE_PENDING", payload: false });
        return;
      }
      console.log("[passkey] showing create prompt");
      setShowCreatePrompt(true);
      setPasskeyState("prompt");
    });
    return () => {
      cancelled = true;
    };
  }, [status, passkeyEnabled, createDismissed, bypassedViaUse, showCreatePrompt, dispatch]);

  // Fire OFFERED the moment either prompt becomes visible. One-shot per mode
  // — re-renders won't re-fire. SELECTED + REGISTERED are written server-side
  // by /register/options + /register/verify, so we don't need to track those.
  useEffect(() => {
    if (showUsePrompt && !useOfferedFiredRef.current) {
      useOfferedFiredRef.current = true;
      recordPasskeyEvent("offered", {
        ...passkeyArgs,
        metadata: { mode: "use" },
      });
    }
  }, [showUsePrompt]);

  useEffect(() => {
    if (showCreatePrompt && !createOfferedFiredRef.current) {
      createOfferedFiredRef.current = true;
      recordPasskeyEvent("offered", {
        ...passkeyArgs,
        metadata: { mode: "create" },
      });
    }
  }, [showCreatePrompt]);

  const handleUseContinue = async () => {
    if (passkeyState === "success") {
      // User has reviewed the bypass payload and clicked Continue. Surface
      // the identity to the publisher and dispatch the terminal status now
      // — this is what closes the iframe / fires the publisher redirect.
      if (bypassDebugPayload) {
        postMessage(
          {
            messageType: "passkey_bypass",
            status: "PASS",
            ...bypassDebugPayload,
          },
          { trigger: "passkey_bypass" },
        );
      }
      dispatch({ type: "SET_STATUS", payload: "PASS" });
      setShowUsePrompt(false);
      setUseDismissed(true);
      return;
    }
    if (passkeyState === "error") {
      setPasskeyState("prompt");
      return;
    }
    setPasskeyState("loading");
    const result = await tryPasskeyBypass(passkeyArgs);
    if (result.bypassed) {
      setBypassedViaUse(true);
      const debugSnapshot = {
        identity: result.identity,
        original_request_id: result.original_request_id,
        credential_id: result.credential_id,
        request_id: settings?.request_id,
      };
      setBypassDebugPayload(debugSnapshot);
      console.log("[passkey] bypass payload available to app:", debugSnapshot);
      // Land on the success screen — the user inspects the payload and
      // explicitly clicks Continue to terminate. postMessage + SET_STATUS
      // PASS happen in the branch above when they do.
      setPasskeyState("success");
    } else {
      setPasskeyState("error");
    }
  };

  const handleUseSkip = () => {
    // Use "skip" rather than "dismissed" — ad-blockers / annoyance lists
    // filter on the word "dismissed" (and on "user_dismissed" in metadata).
    // Backend accepts "skip" as an alias and stores event_type='DECLINED'.
    recordPasskeyEvent("skip", {
      ...passkeyArgs,
      metadata: { mode: "use" },
    });
    // Defer the unmount one tick so Chrome can commit the fetch above to
    // the network stack first. keepalive on its own only protects against
    // *page* unload, not SPA component unmount in the same React commit.
    setTimeout(() => {
      setShowUsePrompt(false);
      setUseDismissed(true);
      setPasskeyState("prompt");
    }, 0);
  };

  const finishCreate = () => {
    setShowCreatePrompt(false);
    setCreateDismissed(true);
    setPasskeyState("prompt");
    dispatch({ type: "SET_PASSKEY_CREATE_PENDING", payload: false });
  };

  const handleCreateContinue = async () => {
    if (passkeyState === "success" || passkeyState === "error") {
      finishCreate();
      return;
    }

    // Cross-origin iframe? WebAuthn create() is hard-blocked there by
    // Safari + Chrome 130+ regardless of Permissions Policy. Open a
    // top-level popup that runs the ceremony in a context with no
    // ancestors, then waits for it to post the result back.
    if (!isSameOriginWithAncestors()) {
      setPasskeyState("loading");
      const popupUrl = new URL(window.location.href);
      popupUrl.searchParams.set("passkey_setup", "1");
      const popup = window.open(
        popupUrl.toString(),
        "passkey-setup",
        "width=420,height=640,menubar=no,toolbar=no,location=no",
      );
      if (!popup) {
        // Popup blocked — show the error state so the user can retry or skip.
        console.warn("[passkey] popup blocked");
        setPasskeyState("error");
        return;
      }
      let settled = false;
      const cleanup = () => {
        window.removeEventListener("message", handleMsg);
        clearInterval(closeChecker);
      };
      const handleMsg = (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== "passkey_setup_result") return;
        settled = true;
        cleanup();
        if (event.data.success) {
          setPasskeyState("success");
        } else if (event.data.error === "registration_failed") {
          // Ceremony failed (user cancelled the biometric prompt, device
          // rejected, /register/verify non-200, etc.) — show the error
          // state in the iframe so the user can Try Again or Continue
          // without passkey.
          setPasskeyState("error");
        } else {
          // User clicked Cancel in the popup or closed it cleanly. Treat
          // as a skip and release the gate so the journey terminates.
          finishCreate();
        }
      };
      window.addEventListener("message", handleMsg);
      const closeChecker = setInterval(() => {
        if (popup.closed) {
          if (!settled) {
            // User closed the popup without finishing — treat as skip.
            cleanup();
            finishCreate();
          }
        }
      }, 400);
      return;
    }

    setPasskeyState("loading");
    // Build identity bindings from the actual session shape — `data.fn`/`ln`
    // are separate fields and the address is split across addr/city/state/zip.
    // Only fields the profile allows via `binding_extras` will actually be
    // persisted server-side, so passing extras is harmless.
    const fullName =
      data?.fn || data?.ln
        ? [data?.fn, data?.ln].filter(Boolean).join(" ")
        : undefined;
    const hasAddressParts =
      data?.addr || data?.city || data?.state || data?.zip;
    const address = hasAddressParts
      ? {
          line1: data?.addr || undefined,
          city: data?.city || undefined,
          region: data?.state || undefined,
          postal: data?.zip || undefined,
        }
      : undefined;
    const result = await registerPasskey({
      ...passkeyArgs,
      binding: {
        full_name: fullName,
        address,
        phone: data?.phone || undefined,
        dob: data?.dob || undefined,
      },
    });
    if (result.registered) {
      setPasskeyState("success");
    } else {
      setPasskeyState("error");
    }
  };

  const handleCreateSkip = () => {
    // Only fire the skip event if the user skipped before clicking "Set Up
    // Passkey". Once they trigger /register/options, the worker writes
    // SELECTED, and subsequent skip (e.g. from the error state) shouldn't
    // double-count. Using "skip" rather than "dismissed" to avoid ad-blocker
    // pattern matches; backend stores event_type='DECLINED' either way.
    if (passkeyState === "prompt") {
      recordPasskeyEvent("skip", {
        ...passkeyArgs,
        metadata: { mode: "create" },
      });
      // Defer the unmount one tick so Chrome can commit the fetch above
      // to the network stack first. keepalive on its own only protects
      // against *page* unload, not SPA component unmount in the same
      // React commit.
      setTimeout(finishCreate, 0);
      return;
    }
    finishCreate();
  };

  // Show a separate close bar at the top-right for wide iframe viewports
  const showWideCloseBar = isIframe && !isMobile && isWideViewport && settings?.isCloseButtonShown !== false;

  const handleClose = () => {
    if (dispatch) {
      dispatch({ type: "SET_CLOSED_CLICKED", payload: true });
    }
    postMessage && postMessage("CLOSE", { trigger: "nav_close" });
  };

  // Update the viewport height on resize
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Theme-aware styles
  const getDesktopContainerStyles = () => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor:
      customerDesktopBg ??
      (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0]),
  });

  const getContentStylesDirectDesktop = () => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "400px",
    height: "700px",
    maxHeight: "90vh",
    overflow: "hidden",
    border: `.5px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
    padding: "25px 25px",
    paddingTop: "10px",
    backgroundColor:
      customerBg ??
      (theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white),
  });

  return (
    <>
      <Notifications position="top-center" zIndex={2077} />
      {showWideCloseBar && (
        <div
          style={{
            position: "fixed",
            top: 0,
            insetInlineStart: 0,
            insetInlineEnd: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            padding: "8px 12px",
            borderBottom: "1px solid #ccc",
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            zIndex: 1000,
          }}
        >
          <ActionIcon variant="light" onClick={handleClose}>
            <IconX stroke={1.5} size={18} />
          </ActionIcon>
        </div>
      )}
      <div
        className={!isMobile && isWideViewport ? "desktopContainer" : ""}
        style={{
          ...(!isMobile && isWideViewport ? getDesktopContainerStyles() : {}),
          ...(isMobile || !isWideViewport ? {
            backgroundColor:
              customerDesktopBg ??
              (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white)
          } : {})
        }}
      >
        <div
          style={{
            ...(isMobile ? {
              height: `calc(${viewportHeight}px - 50px)`,
            } : {}),
            ...(isMobile || !isWideViewport ? {
              backgroundColor:
                customerBg ??
                (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white)
            } : {}),
            ...(!isMobile && isWideViewport ? getContentStylesDirectDesktop() : {})
          }}
          className={
            !isMobile && isWideViewport ? "contentStylesDirectDesktop" : "content"
          }
        >
          {settings?.isHeaderShown !== false && (
            <Navigation
              // Suppress the header logo while the NoticeConsentPrompt
              // is rendering — that screen has its own customer logo
              // in the partnership hero, and showing the same image
              // in the header reads as duplication.
              logo_url={
                showVerificationNotice
                  ? ""
                  : data?.styles?.logo_url ?? data?.logo_url ?? ""
              }
            />
          )}
          {/* Route slot. Scrolls when a screen is taller than the box
              (a phone held sideways, a long form) instead of spilling
              out under the footer, which sits in the 50px strip App
              reserves below this box. It spans the parent's 25px side
              padding (negative margin + matching padding) so screens
              that bleed edge to edge, like NoticeConsentPrompt, are not
              clipped by the scroll container. */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              overflowX: 'hidden',
              margin: '0 -25px',
              padding: '20px 25px 0',
            }}
          >
            {showVerificationNotice ? (
              <NoticeConsentPrompt
                customerName={consentScreen.customerName}
                customerLogoUrl={consentScreen.customerLogoUrl}
                customerPrivacyPolicyUrl={consentScreen.customerPrivacyPolicyUrl}
                veratadPrivacyNoticeUrl="https://veratad.com/privacy-policy"
                purpose={consentScreen.purpose}
                retention={consentScreen.retention}
                requireConsent={isOutsideUS}
                onContinue={() => setConsentDismissed(true)}
              />
            ) : showUsePrompt ? (
              <PasskeyPrompt
                mode="use"
                state={passkeyState}
                onContinue={handleUseContinue}
                onSkip={handleUseSkip}
              />
            ) : showCreatePrompt ? (
              <PasskeyPrompt
                mode="create"
                state={passkeyState}
                onContinue={handleCreateContinue}
                onSkip={handleCreateSkip}
              />
            ) : passkeyChecking ? (
              <LoadingOverlay visible />
            ) : (
              routes
            )}
          </div>
        </div>
      </div>

      {settings?.isFooterShown !== false && (
        <Box
          align="center"
          style={{
            position: "absolute",
            bottom: 15,
            width: "100%",
            align: "center",
          }}
        >
          <Center>
            <Group spacing={10}>
              <Group spacing={5}>
                <Image src={VXLogo} width={20} />
                <Text fz="xs" c="dimmed">
                  {footerLocale?.poweredBy ?? "Powered by Veratad"}
                </Text>
              </Group>
              <Divider orientation="vertical" />
              <Anchor
                href={data?.settings?.journey?.settings?.privacyPolicy || "https://veratad.com/privacy-policy/"}
                size="xs"
                target="_blank"
              >
                {footerLocale?.privacyPolicy ?? "Privacy Policy"}
              </Anchor>
            </Group>
          </Center>
        </Box>
      )}

      <OrientationWarning />
    </>
  );
}

function App({ routes }) {
  const { data, settings } = useContext(DataContext);

  // The session language drives <html lang> (font and hyphenation
  // selection — Traditional vs Simplified Chinese glyphs depend on it)
  // and <html dir>: Arabic, Persian, Hebrew and Urdu render right-to-left
  // with a mirrored layout. Re-applied whenever the language changes
  // mid-session (a handoff landing with a different tag, a publisher
  // update), and the same direction feeds Mantine's theme.dir below so
  // its own components (Drawer, Text, Popover) follow.
  const activeLanguage = settings?.language ?? data?.language ?? "";
  const textDirection = getTextDirection(activeLanguage);
  useEffect(() => {
    applyDocumentLanguage(activeLanguage);
  }, [activeLanguage]);

  if (!data) {
    return <LoadingOverlay visible />;
  }
  const buttonTextColor = data.styles?.components?.button?.color || null;
  // Optional CTA fill independent of primaryColor/primaryShade
  // (components.button.backgroundColor, hoverBackgroundColor).
  const buttonStyleConfig = data.styles?.components?.button || null;
  // components.loader.color: the full-screen loading spinner and any
  // Mantine Loader without an explicit color. components.link.color:
  // text links. Both fall back to the primary color.
  const loaderColor = normalizeColor(data.styles?.components?.loader?.color);
  const linkColor = normalizeColor(data.styles?.components?.link?.color);
  const customerBg = data.styles?.global?.backgroundColor ?? urlBackgroundColor();
  const customerFg = data.styles?.global?.color ?? null;
  const customerFgMuted = data.styles?.global?.colorMuted ?? null;
  const customerInputBg = data.styles?.components?.input?.backgroundColor ?? null;

  // Font configuration
  const fontDef = data.styles?.global?.typography?.font || null;
  const fontStack = familyStack(fontDef?.family);

  const colors_array = data.styles?.colors
    ? { ...data.styles.colors }
    : {
        "vx-green": [
          "#eff6fb",
          "#dee9f1",
          "#b8d2e5",
          "#8fbad8",
          "#6ea5ce",
          "#5998c8",
          "#4d92c6",
          "#3e7eaf",
          "#34709d",
          "#22618b",
        ],
      };

  // When the customer provides colorMuted, override the gray/dark shades that
  // Mantine uses to resolve `c="dimmed"` text. This is a global swap so any
  // gray[6]/dark[2] usage (some borders, icons) follows the customer color too;
  // if that bleed becomes a problem we can switch to per-component overrides.
  if (customerFgMuted) {
    const grayDefaults = [
      '#f8f9fa', '#f1f3f5', '#e9ecef', '#dee2e6', '#ced4da',
      '#adb5bd', '#868e96', '#495057', '#343a40', '#212529',
    ];
    const darkDefaults = [
      '#C1C2C5', '#A6A7AB', '#909296', '#5c5f66', '#373A40',
      '#2C2E33', '#25262b', '#1A1B1E', '#141517', '#101113',
    ];
    const gray = [...(colors_array.gray ?? grayDefaults)];
    gray[6] = customerFgMuted;
    const dark = [...(colors_array.dark ?? darkDefaults)];
    dark[2] = customerFgMuted;
    colors_array.gray = gray;
    colors_array.dark = dark;
  }

  // Debug theme configuration
  const themeConfig = {
    //fontFamily: "Roboto, sans-serif",
    colors: colors_array,
    colorScheme: data.styles?.colorScheme
      ? data.styles.colorScheme
      : "light",
  };

  return (
    <MantineProvider
      theme={{
        ...themeConfig,
        dir: textDirection,
        primaryColor: data.styles?.primaryColor
          ? data.styles.primaryColor
          : "vx-green",
        primaryShade: data.styles?.primaryShade ? data.styles.primaryShade : 5,
        // Use custom family globally when provided (text + headings)
        fontFamily: fontDef ? fontStack : undefined,
        headings: fontDef ? { fontFamily: fontStack } : undefined,
        components: {
          Button: {
            defaultProps: {
              radius: data.styles?.button?.radius
                ? data.styles.button.radius
                : 6,
            },
            styles: (theme, params, { variant }) => {
              // In Mantine v5, variant is in the third context arg, not params
              const isFilledVariant = variant === "filled" || !variant;

              return {
                root: {
                  // Customer CTA fill (independent of the primary
                  // palette). Empty object when unset, so Mantine's
                  // palette-driven fill/hover stay exactly as before.
                  ...(isFilledVariant
                    ? ctaButtonRootStyles(theme, buttonStyleConfig)
                    : secondaryButtonRootStyles(theme, variant, buttonStyleConfig)),
                  // Only apply buttonTextColor to filled (primary) buttons
                  ...(isFilledVariant && buttonTextColor
                    ? {
                        color: buttonTextColor,
                        // Keep icons in sync via currentColor only. Our icons
                        // (Tabler) are stroke-based and ship with fill="none";
                        // forcing fill: currentColor here overrode that and
                        // flooded outline icons into solid blobs whenever a
                        // customer set buttonTextColor. Setting color alone
                        // drives stroke="currentColor" for outline icons and
                        // fill="currentColor" for genuine filled icons, so both
                        // follow the text color without clobbering fill="none".
                        "& svg": { color: buttonTextColor },
                      }
                    : {}),
                },
              };
            },
          },
          // Overlay backdrop is opaque and matches the app body color so the
          // "locked" visual cue is preserved without tinting a customer background.
          LoadingOverlay: {
            defaultProps: (theme) => ({
              overlayColor:
                customerBg ??
                (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white),
              overlayOpacity: 1,
              ...(loaderColor ? { loaderProps: { color: loaderColor } } : {}),
            }),
          },
          ...(loaderColor
            ? { Loader: { defaultProps: { color: loaderColor } } }
            : {}),
          ...(linkColor
            ? { Anchor: { styles: () => ({ root: { color: linkColor } }) } }
            : {}),
          // Customer-controlled input surface and text color. Affects every
          // Mantine input that renders through the shared Input wrapper
          // (TextInput, NumberInput, Select, PasswordInput, etc.) without
          // touching the call sites.
          ...(customerInputBg || customerFg
            ? {
                Input: {
                  styles: () => ({
                    input: {
                      ...(customerInputBg ? { backgroundColor: customerInputBg } : {}),
                      ...(customerFg ? { color: customerFg } : {}),
                    },
                  }),
                },
              }
            : {}),
        },
        defaultRadius: data.styles?.defaultRadius ?? 6,
        breakpoints: {
          xs: 340, // Extra small screen breakpoint in pixels
          sm: 640, // Small screen breakpoint in pixels
          md: 960, // Medium screen breakpoint in pixels
          lg: 1280, // Large screen breakpoint in pixels
          xl: 1440, // Extra large screen breakpoint in pixels
        },
      }}
      withGlobalStyles
      withNormalizeCSS
    >
      {fontDef && <FontLoaderSimple styles={data.styles} />}
      <Global
        styles={(theme) => ({
          body: {
            backgroundColor:
              customerBg ??
              (theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white),
            ...(customerFg ? { color: customerFg } : {}),
          },
          // Mantine 6 positions a few of its internals with physical
          // left/right. Under dir="rtl" flexbox already reverses the
          // order (a leftIcon lands on the right), so only the margins
          // and the absolutely positioned input adornments need a hand.
          '[dir="rtl"] .mantine-Button-leftIcon': {
            marginRight: 0,
            marginLeft: theme.spacing.xs,
          },
          '[dir="rtl"] .mantine-Button-rightIcon': {
            marginLeft: 0,
            marginRight: theme.spacing.xs,
          },
          '[dir="rtl"] .mantine-Input-icon': { left: "auto", right: 0 },
          '[dir="rtl"] .mantine-Input-rightSection': { right: "auto", left: 0 },
          '[dir="rtl"] .mantine-Alert-icon': {
            marginRight: 0,
            marginLeft: theme.spacing.md,
          },
        })}
      />
      <AppContent routes={routes} />
    </MantineProvider>
  );
}

export default App;
